import React, { useState } from 'react'

import AddComment from './AddComment'
import {
  BoardGroupFragment,
  useAddCommentMutation,
  BoardGroupFragmentDoc
} from '../../../../../generated/graphql'
import useNotification from '../../../../../lib/hooks/useNotification'

type AddCommentWrapperProps = {
  /** Current Plan Group */
  group: BoardGroupFragment
  /** Indicates if this plan is a template */
  isTemplate: boolean
  /** Max length of the comment */
  maxLength?: number
  /** Event callback when the cancel or close buttons are clicked */
  onCancel: () => void
  /** Event callback when the save comment button is clicked */
  onSaveComment?: () => void
  /** Show the add comment field when clicking Add Comment from the group menu */
  showAddComment: boolean
  /** Show the comments when clicking on the comment count button */
  showComments: boolean
}

const defaultProps: Pick<AddCommentWrapperProps, 'maxLength' | 'onSaveComment'> = {
  maxLength: 200,
  onSaveComment: undefined
}

const AddCommentWrapper = ({
  group,
  isTemplate,
  maxLength,
  onCancel,
  onSaveComment,
  showAddComment,
  showComments
}: AddCommentWrapperProps) => {
  const [comment, setComment] = useState('')
  const { showErrorNotif } = useNotification()
  const [addComment] = useAddCommentMutation({
    onCompleted: () => {
      if (onSaveComment) {
        onSaveComment()
      }
      setComment('')
    },
    onError: () => {
      showErrorNotif('Could not add comment.')
    }
  })

  const onCancelAddComment = () => {
    setComment('')
    onCancel()
  }

  const onCommentChange = (value: string) => {
    setComment(value)
  }

  const handleSaveComment = () => {
    addComment({
      variables: {
        input: {
          comment,
          groupId: group.id
        }
      },
      update: (proxy, { data }) => {
        const addedComment = data?.addComment
        const cacheId = `PlanGroup:${group.id}`
        const cacheGroup = proxy.readFragment<BoardGroupFragment>({
          id: cacheId,
          fragment: BoardGroupFragmentDoc,
          fragmentName: 'boardGroup'
        })

        if (cacheGroup) {
          const newCacheGroup = {
            ...group,
            comments: [addedComment, ...group.comments]
          }

          proxy.writeFragment({
            id: cacheId,
            fragment: BoardGroupFragmentDoc,
            data: newCacheGroup,
            fragmentName: 'boardGroup'
          })
        }
      }
    })
  }

  return (
    <AddComment
      comment={comment}
      isTemplate={isTemplate}
      maxLength={maxLength}
      onCancel={onCancelAddComment}
      onCommentChange={onCommentChange}
      onSaveComment={handleSaveComment}
      showAddComment={showAddComment}
      showComments={showComments}
    />
  )
}

AddCommentWrapper.defaultProps = defaultProps

export default AddCommentWrapper
